import type { AxiosError } from 'axios'

type ErrorMessage = Record<
  number,
  {
    status: number
    message: string
  }
>

type ApiErrorResponse = {
  message?: string | string[]
  data?: ApiError['data']
  validation?: {
    body?: {
      message?: string | string[]
      keys?: string[]
    }
  }
}

const extractMessageText = (value: unknown) => {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value.find((item) => typeof item === 'string') || ''
  }
  return ''
}

const errors: ErrorMessage = {
  401: {
    status: 401,
    message: 'Usuário não autorizado',
  },
  403: {
    status: 403,
    message: 'Usuário não autorizado',
  },
}

const getReadableFieldName = (field: string) =>
  field.replace(/['"]/g, '').replace(/_/g, ' ').trim()

const getReadableType = (value: string) => {
  const lowerValue = value.trim().toLowerCase()

  if (lowerValue.includes('string')) return 'um texto'
  if (lowerValue.includes('number')) return 'um numero'
  if (lowerValue.includes('boolean')) return 'verdadeiro ou falso'
  if (lowerValue.includes('array')) return 'uma lista'
  if (lowerValue.includes('object')) return 'um objeto'

  return value
}

const getCelebrateValidationMessage = (errorData: ApiErrorResponse) => {
  const apiMessage = extractMessageText(errorData.message).toLowerCase()
  const validationMessage = extractMessageText(
    errorData.validation?.body?.message,
  )
  const validationKey = errorData.validation?.body?.keys?.[0]

  if (!apiMessage.includes('celebrate request validation failed')) {
    return ''
  }

  const matchedMessage = validationMessage.match(
    /"([^"]+)"\s+must\s+be\s+(.+)$/i,
  )

  if (matchedMessage) {
    const [, fieldName, expectedType] = matchedMessage
    return `Campo ${getReadableFieldName(
      fieldName,
    )} invalido: informe ${getReadableType(expectedType)}.`
  }

  if (validationKey) {
    return `Campo ${getReadableFieldName(
      validationKey,
    )} invalido. Revise o valor informado.`
  }

  return 'Existem campos invalidos no formulario. Revise os dados e tente novamente.'
}

const handleGetErroMessage = (message: unknown) => {
  const parsedMessage = extractMessageText(message)
  const value = parsedMessage.toLowerCase()
  if (value.includes('already exists')) {
    return 'Esse registro já foi cadastrado'
  }
  if (value.includes('code activity incorrect')) {
    return 'O Código da atividade no arquivo é diferente do escolhido'
  }
  if (
    value.includes(
      'esta informação não pode ser removida, pois está sendo usada por outra tabela',
    )
  ) {
    return 'Esta informação não pode ser removida, pois está sendo usada em outro lugar!'
  }
  if (value.includes('this affiliate is being used in activity')) {
    return 'Esta informação não pode ser removida, pois está sendo usada em uma atividade!'
  }
  return parsedMessage
}

export const handleErrorMessage = ({
  axiosError,
  customErrors = {},
}: {
  axiosError: AxiosError
  customErrors?: ErrorMessage
}) => {
  const errorData = (axiosError.response?.data as ApiErrorResponse) || {}

  const celebrateValidationMessage = getCelebrateValidationMessage(errorData)

  const message =
    celebrateValidationMessage || handleGetErroMessage(errorData.message)

  return (
    { ...errors, ...customErrors }[axiosError.response?.status as number] || {
      status: axiosError.response?.status,
      message: message || '',
      data: errorData.data,
    }
  )
}
